import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/apiClient";
import { formatRupiah } from "@/lib/assessmentConfig";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Loader2, Trophy, Sparkles, RefreshCw, ArrowLeft, ArrowRight, Wallet,
  AlertTriangle, ShieldAlert, HelpCircle, CheckCircle2, Info, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const CONF_STYLE = { "Tinggi": "bg-brand text-white", "Sedang": "bg-brand-amber text-white", "Rendah": "bg-slate-300 text-slate-700" };
const CANVAS = [
  ["customer", "Customer", "Siapa yang membeli?"],
  ["value", "Value", "Kenapa mereka beli?"],
  ["channel", "Channel", "Bagaimana mereka menemukan & memesan?"],
  ["relationship", "Relationship", "Bagaimana repeat order didorong?"],
  ["revenue", "Revenue", "Bagaimana bisnis menghasilkan uang?"],
  ["key_activities", "Key Activities", "Yang harus rutin dikerjakan pemilik"],
  ["key_resources", "Key Resources", "Aset/sumber daya yang berguna"],
  ["key_partners", "Key Partners", "Supplier/partner yang mungkin diperlukan"],
  ["costs", "Costs", "Kategori biaya utama"],
];

export default function Blueprint() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [bp, setBp] = useState(null);
  const [hasWinner, setHasWinner] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [bRes, wRes] = await Promise.all([
          api.get("/blueprint").catch(() => ({ data: null })),
          api.get("/winner").catch(() => ({ data: null })),
        ]);
        setBp(bRes.data || null);
        setHasWinner(!!wRes.data?.winner_name);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const generate = async () => {
    setGenerating(true);
    setError("");
    try {
      const { data } = await api.post("/blueprint/generate");
      setBp(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Gagal membuat blueprint.");
      toast.error("Gagal membuat blueprint.");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <DashboardLayout><div className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand-accent" /></div></DashboardLayout>;

  if (generating) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-xl text-center py-20 animate-fade-up" data-testid="blueprint-generating">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10 text-brand"><Loader2 className="h-8 w-8 animate-spin" /></div>
          <h2 className="mt-6 font-heading text-xl font-bold text-ink">Menyusun blueprint Anda…</h2>
          <p className="mt-3 text-ink-soft leading-relaxed">IDEbisnis AI sedang merancang model bisnis praktis untuk Business Winner Anda sesuai modal, kemampuan, dan tujuan Anda.</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!bp && !hasWinner) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-2xl text-center animate-fade-up" data-testid="blueprint-need-winner">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10 text-brand"><Trophy className="h-8 w-8" /></div>
          <h1 className="mt-6 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Tentukan Business Winner Dulu</h1>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">Blueprint dibuat dari Business Winner Anda. Selesaikan perbandingan & pemenang terlebih dahulu.</p>
          <Button onClick={() => navigate("/comparison")} data-testid="go-comparison" className="mt-6 h-12 bg-brand hover:bg-brand-hover px-6">Ke Business Winner</Button>
        </div>
      </DashboardLayout>
    );
  }

  if (!bp) {
    return (
      <DashboardLayout>
        <div className="mx-auto max-w-2xl text-center animate-fade-up" data-testid="blueprint-generate-cta">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-brand/10 text-brand"><Sparkles className="h-8 w-8" /></div>
          <h1 className="mt-6 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Buat Blueprint Bisnis Anda</h1>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">IDEbisnis AI akan mengubah Business Winner Anda menjadi rancangan praktis untuk diuji.</p>
          {error && <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="blueprint-error">{error}</div>}
          <Button onClick={generate} data-testid="generate-blueprint-btn" className="mt-6 h-12 bg-brand hover:bg-brand-hover px-6 text-base"><Sparkles className="mr-1.5 h-5 w-5" /> Buat Blueprint</Button>
        </div>
      </DashboardLayout>
    );
  }

  const cap = bp.capital || {};
  return (
    <DashboardLayout>
      <div className="mx-auto max-w-2xl animate-fade-up" data-testid="blueprint-results">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate("/comparison")} className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-brand transition-colors" data-testid="blueprint-back"><ArrowLeft className="h-4 w-4" /> Business Winner</button>
          <Button variant="outline" onClick={generate} data-testid="regenerate-blueprint" className="border-borderc gap-1.5"><RefreshCw className="h-4 w-4" /> Buat Ulang</Button>
        </div>

        <h1 className="mt-4 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">Blueprint Bisnis Anda</h1>
        <p className="mt-3 text-base text-ink-soft leading-relaxed">Rancangan awal untuk menjalankan Business Winner sesuai kondisi, modal, kemampuan, dan tujuan Anda.</p>

        {/* Winner reference */}
        <div className="mt-5 rounded-2xl border-2 border-brand bg-brand/5 p-5" data-testid="winner-ref">
          <div className="flex items-center gap-1.5 text-brand"><Trophy className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wide">Business Winner</span></div>
          <div className="mt-1 font-heading text-lg font-extrabold text-ink" data-testid="blueprint-winner-name">{bp.winner_name}</div>
          {bp.confidence && <span className={`mt-2 inline-block rounded-full px-2.5 py-0.5 text-xs font-bold ${CONF_STYLE[bp.confidence]}`}>Tingkat keyakinan: {bp.confidence}</span>}
        </div>
        <p className="mt-3 flex items-start gap-1.5 text-xs text-ink-muted"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Blueprint ini adalah rancangan awal untuk diuji, bukan jaminan keberhasilan bisnis.</p>

        {/* Section 1 */}
        <Section n="1" title="Bisnis Anda" testid="sec-bisnis">
          <Field label="Konsep bisnis" value={bp.concept} />
          <Field label="Target customer awal" value={bp.target_customer} />
          <Field label="Masalah/kebutuhan customer" value={bp.customer_problem} />
          <Field label="Value proposition" value={bp.value_proposition} />
          <Field label="Kenapa cocok untuk Anda" value={bp.why_fit} />
        </Section>

        {/* Section 2 */}
        <Section n="2" title="Penawaran Pertama yang Bisa Anda Uji" testid="sec-mvo">
          <Field label="Apa yang dijual dulu" value={bp.mvo?.what_to_sell} />
          <Field label="Customer pertama yang diuji" value={bp.mvo?.first_customer} />
          <Field label="Bentuk penawaran awal" value={bp.mvo?.offer_form} />
          <Field label="Channel order/penjualan" value={bp.mvo?.sales_channel} />
          <Field label="Cara pemenuhan (fulfillment)" value={bp.mvo?.fulfillment} />
          <Field label="Uji terkecil yang praktis" value={bp.mvo?.smallest_test} />
          <Field label="Tanda ada minat customer" value={bp.mvo?.interest_evidence} />
        </Section>

        {/* Section 3 */}
        <Section n="3" title="Cara Bisnis Menghasilkan Uang" testid="sec-money">
          <Field label="Revenue model" value={bp.money?.revenue_model} />
          <Field label="Channel penjualan utama" value={bp.money?.primary_channel} />
          <Field label="Bagaimana transaksi pertama terjadi" value={bp.money?.first_transaction} />
          <Field label="Mekanisme repeat/berulang" value={bp.money?.repeat_mechanism} />
          <Field label="Faktor utama margin" value={bp.money?.margin_factor} />
          <Field label="Faktor utama cashflow" value={bp.money?.cashflow_factor} />
          <Field label="Cashflow cepat vs nilai jangka panjang" value={bp.money?.fast_vs_longterm} />
        </Section>

        {/* Section 4 — Capital */}
        <Section n="4" title="Modal & Angka Awal" testid="sec-capital">
          <div className="grid grid-cols-3 gap-2">
            <CapNum label="Total Modal" value={bp.available_capital} tone="ink" />
            <CapNum label="Cadangan (jangan dipakai)" value={bp.capital_reserve} tone="amber" />
            <CapNum label="Dapat Digunakan" value={bp.deployable_capital} tone="brand" />
          </div>
          <div className="mt-3 rounded-xl border border-brand-accent bg-brand/5 p-4" data-testid="test-budget">
            <div className="text-xs font-bold text-brand">BUDGET UJI AWAL</div>
            <div className="mt-1 font-heading text-lg font-extrabold text-ink">{cap.test_budget || "-"}</div>
            <p className="mt-1 text-[11px] text-ink-muted">Modal yang dapat digunakan adalah batas maksimum, bukan target belanja. Gunakan jumlah terkecil yang cukup untuk menguji permintaan.</p>
          </div>

          {cap.has_enough_data === false ? (
            <div className="mt-3 rounded-xl border border-brand-amber/40 bg-brand-amber/5 p-4" data-testid="capital-insufficient">
              <div className="flex items-center gap-1.5 text-brand-amber"><AlertTriangle className="h-4 w-4" /><span className="text-sm font-semibold">Belum cukup data untuk menghitung secara layak.</span></div>
              {cap.data_needed?.length ? (
                <>
                  <p className="mt-2 text-xs font-semibold text-ink">Data yang perlu Anda cek:</p>
                  <ul className="mt-1 list-disc pl-5 text-sm text-ink-soft space-y-1">{cap.data_needed.map((x, i) => <li key={i}>{x}</li>)}</ul>
                </>
              ) : null}
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              <div className="rounded-md bg-brand-amber/10 px-3 py-1.5 text-[11px] font-semibold text-brand-amber">ESTIMASI — perlu divalidasi dengan harga lokal.</div>
              <Field label="Perkiraan biaya awal/uji" value={cap.startup_cost} />
              <Field label="Kebutuhan modal kerja" value={cap.working_capital} />
              <Field label="Kerangka biaya/HPP" value={cap.cost_framework} />
              <Field label="Hipotesis harga awal" value={cap.pricing_hypothesis} />
              <Field label="Kerangka margin kotor" value={cap.margin_framework} />
              <Field label="Kerangka break-even" value={cap.breakeven_framework} />
            </div>
          )}
          {cap.assumptions?.length ? (
            <div className="mt-3">
              <p className="text-xs font-semibold text-ink">Asumsi:</p>
              <ul className="mt-1 list-disc pl-5 text-sm text-ink-soft space-y-1">{cap.assumptions.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </div>
          ) : null}
        </Section>

        {/* Section 5 — Canvas */}
        <Section n="5" title="Model Bisnis Ringkas" testid="sec-canvas">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CANVAS.map(([key, label, hint]) => (
              <div key={key} className="rounded-xl border border-borderc bg-surface p-3" data-testid={`canvas-${key}`}>
                <div className="text-xs font-bold text-brand-accent">{label}</div>
                <div className="text-[10px] text-ink-muted">{hint}</div>
                <p className="mt-1.5 text-sm text-ink-soft leading-snug">{bp.canvas?.[key] || "-"}</p>
              </div>
            ))}
          </div>
        </Section>

        {/* Section 6 */}
        <Section n="6" title="Sebelum Anda Mengeluarkan Modal Lebih Besar" testid="sec-before">
          <div className="grid gap-3 sm:grid-cols-3">
            <MiniCard icon={ShieldAlert} title="Risiko Terbesar" items={bp.biggest_risks} tone="amber" />
            <MiniCard icon={HelpCircle} title="Asumsi yang Masih Harus Dibuktikan" items={bp.assumptions_to_prove} tone="accent" />
            <MiniCard icon={CheckCircle2} title="Tanda Bisnis Ini Layak Dilanjutkan" items={bp.go_signals} tone="brand" />
          </div>
        </Section>

        {/* Capital protection */}
        {bp.capital_protection && (
          <div className="mt-5 flex items-start gap-2 rounded-2xl border-2 border-brand-amber/40 bg-brand-amber/5 p-5" data-testid="capital-protection">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-brand-amber" />
            <div><h3 className="font-heading text-sm font-bold text-ink">Jaga Modal Anda</h3><p className="mt-1 text-sm text-ink-soft leading-relaxed">{bp.capital_protection}</p></div>
          </div>
        )}

        {/* Next step */}
        <div className="mt-8 rounded-2xl border border-borderc bg-white p-5 text-center">
          <p className="text-sm text-ink-soft leading-relaxed">Blueprint ini belum berarti Anda harus langsung mengeluarkan modal besar. Langkah berikutnya adalah menguji model ini dengan tindakan kecil dan terukur.</p>
          <Button onClick={() => navigate("/launch")} data-testid="next-launch-cta" className="mt-4 h-12 bg-brand hover:bg-brand-hover px-6 text-base">Buat Rencana 7 Hari <ArrowRight className="ml-1 h-5 w-5" /></Button>
        </div>
      </div>
    </DashboardLayout>
  );
}

function Section({ n, title, children, testid }) {
  return (
    <section className="mt-6 rounded-2xl border border-borderc bg-white p-5 sm:p-6" data-testid={testid}>
      <h2 className="font-heading text-lg font-semibold text-ink"><span className="text-brand-accent">{n}.</span> {title}</h2>
      <div className="mt-4 space-y-3">{children}</div>
    </section>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="text-xs font-semibold text-ink-muted">{label}</div>
      <p className="mt-0.5 text-sm text-ink leading-relaxed">{value}</p>
    </div>
  );
}

function CapNum({ label, value, tone }) {
  const color = tone === "brand" ? "text-brand" : tone === "amber" ? "text-brand-amber" : "text-ink";
  return (
    <div className="rounded-xl border border-borderc bg-surface p-3 text-center">
      <div className="text-[10px] text-ink-muted leading-tight">{label}</div>
      <div className={`mt-1 font-heading text-sm font-extrabold ${color}`}>{formatRupiah(value) || "-"}</div>
    </div>
  );
}

function MiniCard({ icon: Icon, title, items, tone }) {
  const c = tone === "brand" ? "text-brand" : tone === "amber" ? "text-brand-amber" : "text-brand-accent";
  return (
    <div className="rounded-xl border border-borderc bg-surface p-4">
      <div className={`flex items-center gap-1.5 ${c}`}><Icon className="h-4 w-4" /><span className="text-xs font-bold">{title}</span></div>
      <ul className="mt-2 list-disc pl-5 text-sm text-ink-soft space-y-1">{(items || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
    </div>
  );
}
